import React from 'react';
import { useRouter } from 'expo-router';
import { WebPageHeader } from '../common/WebPageHeader';

interface WebEditPageShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Right-side action slot rendered next to the title. */
  actions?: React.ReactNode;
  /**
   * Overrides the back arrow. Multi-step pages pass their own so the arrow
   * walks back through the steps instead of abandoning the page from step 2.
   */
  onBack?: () => void;
  /**
   * Hide the header back arrow. Wizards that already carry a "Back" control in
   * their own footer set this so the title lines up with the body content
   * instead of being indented by a 40px slot.
   */
  showBack?: boolean;
  /**
   * Column width for header and body. Narrow it on form-shaped pages so the
   * heading sits over its own content instead of floating out to the left of a
   * narrower, centred form.
   */
  maxWidth?: number;
}

/**
 * Gemini-styled shell for CV edit subpages.
 * - 820px max-width, 64px top padding (per design system §6).
 * - Page header is the shared `WebPageHeader` (32px/400 title, 40px back arrow).
 * - Background uses `var(--bg)`.
 */
export function WebEditPageShell({
  title,
  subtitle,
  children,
  actions,
  onBack,
  showBack = true,
  maxWidth = 820,
}: WebEditPageShellProps) {
  const router = useRouter();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'var(--bg)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div
          className="tabbio-phone-edit-page"
          style={{
            maxWidth,
            width: '100%',
            marginInline: 'auto',
            // Same box as `.tabbio-svc-page`, which every other web route
            // uses. 64px of top padding made this page start noticeably
            // lower than the rest of the app.
            padding: '32px 24px 80px',
          }}
        >
          <WebPageHeader
            title={title}
            subtitle={subtitle}
            onBack={showBack ? (onBack ?? (() => router.back())) : undefined}
          >
            {actions}
          </WebPageHeader>
          <div>{children}</div>
        </div>
      </div>
    </div>
  );
}
